import React, { useEffect, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Launch } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { getCollectionDocs } from '../../api/document-api/DocumentAPI';
import { UploadStatusMetadata } from '../../api/types/JobStatusTypes';

interface LinkCellProps {
  jobId: string;
}

/**
 * Retrieves the most recent successful status doc for a given job and displays a link if available
 * @param jobId id of job we are getting status for
 */
function LinkCell({ jobId }: LinkCellProps) {
  const [successLink, setSuccessLink] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const getSuccessLink = async () => {
      try {
        // Get all status documents for this job
        const statusDocs = await getCollectionDocs(`jobStatus/${jobId}/status`);

        // Sort by timestamp descending to get most recent first
        const sortedStatuses = statusDocs.sort((a, b) => b.timestamp - a.timestamp);

        // Find the most recent successful status
        const successStatus = sortedStatuses.find((status) => {
          const metadata = status.metadata as UploadStatusMetadata;
          return metadata?.severity === 'success' && metadata?.link;
        });

        if (successStatus) {
          const metadata = successStatus.metadata as UploadStatusMetadata;
          setSuccessLink(metadata.link || null);
        }
      } catch (error) {
        console.error('Failed to retrieve success link:', error);
        enqueueSnackbar('Failed to retrieve success link', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    getSuccessLink();
  }, [jobId]);

  const handleLinkClick = () => {
    if (successLink) {
      window.open(successLink, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return <div>...</div>;
  }

  if (!successLink) {
    return <div>-</div>;
  }

  return (
    <Tooltip title="Open link in new tab">
      <IconButton
        onClick={handleLinkClick}
        size="small"
        color="primary"
      >
        <Launch fontSize="medium" />
      </IconButton>
    </Tooltip>
  );
}

export default LinkCell;
